import React from 'react';
import dayjs from 'dayjs';
import { PhotoProvider, PhotoView } from 'react-photo-view';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';

import { usePutEncAfterBlitzModalStore } from './Modals/PutEncAfterBlitzModal/PutEncAfterBlitzModalStore';
import { useCompleteEncModalStore } from './Modals/CompleteEncModal/CompleteEncModalStore';

import { MLButton } from 'components/Button/Button';

interface EncounterPopupProps {
  encounter: any;
  onMove?: () => void;
  onSave?: () => void;
  isMoving?: boolean;
  isLoading?: boolean;
  isActiveFilterArchive?: boolean;
}

export const EncounterPopup: React.FC<EncounterPopupProps> = ({
  encounter,
  onMove,
  onSave,
  isMoving,
  isLoading,
  isActiveFilterArchive,
}) => {
  const { onManagePutEncAfterBlitzOpen } = usePutEncAfterBlitzModalStore();
  const { onCompleteEncOpen } = useCompleteEncModalStore();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const handleResolveClick = () => {
    if (isActiveFilterArchive) {
      enqueueSnackbar(t('InteractiveMap.addMenu.alertOpenArhive'), { variant: 'warning' });
      return;
    }
    onCompleteEncOpen(encounter.id);
  };

  const handleCompleteBlitzClick = () => {
    if (isActiveFilterArchive) {
      enqueueSnackbar(t('InteractiveMap.addMenu.alertOpenArhive'), { variant: 'warning' });
      return;
    }
    onManagePutEncAfterBlitzOpen(encounter.id);
  };

  return (
    <div className="trap-popup">
      <div className="trap-popup-header mb-2 fs-16">
        <strong>{t('Popups.EncounterPopup.title', { id: encounter.id })}</strong>
      </div>
      <div>{encounter.pest ? t('Popups.EncounterPopup.typePest') : t('Popups.EncounterPopup.typeTraces')}</div>
      {encounter.pest ? (
        <div className="">
          <div>{t('Popups.EncounterPopup.pest', { name: encounter.pest.name_US })}</div>
          <div>{t('Popups.EncounterPopup.quantity', { amount: encounter.amount })}</div>
        </div>
      ) : (
        <div className="">{t('Popups.EncounterPopup.traces', { name: encounter.pest_traces.name_US })}</div>
      )}
      <div>{t('Popups.EncounterPopup.place', { place: encounter.place })}</div>
      <div>
        {t('Popups.EncounterPopup.detectedAt', {
          date: dayjs(encounter.discovery_datetime).format('DD.MM.YYYY HH:mm'),
        })}
      </div>
      <div className="border-1 border-top mt-2 pt-1">
        {t('Popups.EncounterPopup.witness', { name: encounter.witness?.name || 'N/A' })}
      </div>
      <div>{t('Popups.EncounterPopup.responsible', { name: encounter.responsible_person?.name || 'N/A' })}</div>

      {encounter.is_blitz_creation ? (
        <div className="row align-items-center text-center">
          <div className="col-xl-12 pt-1">
            <MLButton
              padding={10}
              color="warning"
              onClick={handleCompleteBlitzClick}
              innerText={t('Popups.EncounterPopup.completeEncounter')}
            />
          </div>
        </div>
      ) : (
        <div>{t('Popups.EncounterPopup.description', { comment: encounter?.creation_comment || '' })}</div>
      )}
      {encounter.before_photos && encounter.before_photos?.length !== 0 && (
        <>
          <div className="row align-items-center text-start mt-1">
            <div className="col-xl-12">
              <div className="fw-medium">{t('Popups.EncounterPopup.photos')}</div>
            </div>
          </div>
          <div className="row align-items-center text-start">
            <div className="col-xl-6">
              {encounter.before_photos && (
                <div className="d-flex flex-wrap gap-2">
                  <PhotoProvider>
                    {encounter.before_photos.map((photo: any) => (
                      <PhotoView src={photo}>
                        <img
                          src={photo}
                          alt=""
                          title={t('Popups.EncounterPopup.inspectPhoto')}
                          className="rounded avatar-xs"
                          style={{ cursor: 'pointer' }}
                        />
                      </PhotoView>
                    ))}
                  </PhotoProvider>
                </div>
              )}
            </div>
            <div className="col-xl-6">
              {encounter.after_photos && (
                <div className="d-flex flex-wrap gap-2">
                  <PhotoProvider>
                    {encounter.after_photos.map((photo: any) => (
                      <PhotoView src={photo}>
                        <img
                          src={photo}
                          alt=""
                          title={t('Popups.EncounterPopup.inspectPhoto')}
                          className="rounded avatar-xs"
                          style={{ cursor: 'pointer' }}
                        />
                      </PhotoView>
                    ))}
                  </PhotoProvider>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      <div className="row align-content-center text-center mt-2">
        <div className="col-12 align-items-center text-center">
          {isMoving ? (
            <button
              type="button"
              className="btn btn-ghost-success custom-toggle"
              data-bs-toggle="button"
              onClick={onSave}
              disabled={isLoading}
            >
              <span className="icon-on">
                <i className="ri-save-3-fill align-bottom" />
              </span>
              <span className="icon-off">
                <i className="ri-save-3-fill align-bottom" />
              </span>
              {isLoading ? t('Popups.EncounterPopup.saving') : t('Popups.EncounterPopup.saveLocation')}
            </button>
          ) : (
            encounter.x_coord === 0 &&
            encounter.y_coord === 0 && (
              <>
                <button
                  type="button"
                  className="btn btn-ghost-success custom-toggle mb-2"
                  data-bs-toggle="button"
                  onClick={onMove}
                  disabled={isLoading}
                >
                  <div className="d-flex gap-2">
                    <div className="d-flex">
                      <span className="icon-on">
                        <i className="ri-drag-move-2-fill align-bottom" />
                      </span>
                      <span className="icon-off">
                        <i className="ri-drag-move-2-fill align-bottom" />
                      </span>
                    </div>
                    <div>{t('Popups.EncounterPopup.changeLocation')}</div>
                  </div>
                </button>
                <div
                  className="text-danger"
                  dangerouslySetInnerHTML={{ __html: t('Popups.EncounterPopup.locationWarning') }}
                />
              </>
            )
          )}
        </div>
      </div>

      {!encounter.fix_datetime && (
        <div className="row align-items-center text-center">
          <div className="col-xl-12">
            <button
              type="button"
              className="mt-2"
              style={{
                border: '1px solid #6A37D4',
                backgroundColor: 'transparent',
                color: '#333',
                padding: '8px 16px',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 'bold',
              }}
              onClick={handleResolveClick}
            >
              {t('Popups.EncounterPopup.resolveEncounter')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
